/*===================================================================
=       USE SPENDING DEMAND                 =
====================================================================*/
/**
 *
 *
 */

import { useLocation, useParams } from "react-router-dom";
import { useSingleSpendingDemandCore } from "./core/useSingleSpendingDemand";
import useSpendingDemandCore from "./core/useSpendingDemandCore";
import {
  FilterSpendingDemand,
  SpendingDemandProcessBy,
} from "../types/spendingDemand.types";
import useUserAuth from "../../auth/hook/useUserAuth";
import useErrorHandler from "../../../hook/useErrorHandler";
import {
  Filter,
  OrderByType,
  useGenericAPI,
} from "../../../utils/genericAPI/useGenericAPI";
import { API_BASE_URL_SPENDING_DEMAND_MANAGER } from "../services/SpendingDemandAPI";
import { Spending } from "../../spending/types/spending.types";
import { useEffect, useState } from "react";
import { Credit, SubCredit } from "../../credit/types/credit.type";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";

const BASE_URL = process.env.REACT_APP_URL_BACKEND;

export const useSpendingDemand = (options?: {
  forUser?: boolean;
  disableQuery?: boolean;
}) => {
  const location: string = useLocation().search;
  let pageParams = new URLSearchParams(location).get("page");

  const { userInfo } = useUserAuth();
  const getCurrentPage = () => (pageParams ? parseInt(pageParams) : null);
  const { addTokenIntheHeaderRequest } = useUserAuth();
  addTokenIntheHeaderRequest();
  const getCurrentFilter: () => FilterSpendingDemand = () => {
    let statusParams = new URLSearchParams(location).get(
      "statusSpendingDemand"
    );
    let orderByParams = new URLSearchParams(location).get(
      "orderBySpendingDemand"
    );

    const statusList = statusParams ? statusParams.split(",") : undefined;
    const orderBy = orderByParams || undefined;

    return {
      status: statusList,
      orderBy,
    };
  };

  const { handleInternError, handleNetworkError } = useErrorHandler();

  const settings = {
    handleNetworkErrorFunction: handleNetworkError,
    handleInternErrorFunction: handleInternError,
    getCurrentPage,
    getCurrentFilter,
    employeeId: userInfo?.user_employee_id || "null",
    userId: userInfo?.user_id || "null",
    forUser: options?.forUser || false,
    disableQuery: options?.disableQuery || false,
  };
  return useSpendingDemandCore(settings);
};

export const useSingleSpendingDemand = (
  idSingleSpendingDemand?: string,
  options?: { disableQuery?: boolean }
) => {
  const { spendingDemandId } = useParams();
  const IdFound = idSingleSpendingDemand || spendingDemandId || "";
  const { userInfo, addTokenIntheHeaderRequest } = useUserAuth();
  const { handleInternError, handleNetworkError } = useErrorHandler();
  addTokenIntheHeaderRequest();
  return useSingleSpendingDemandCore(
    IdFound,
    handleNetworkError,
    handleInternError,
    userInfo?.user_employee_id || "",
    options?.disableQuery || false
  );
};

export const useSpendingDemandManager = (config?: {
  pageSize?: number;
  orderBy?: OrderByType<SpendingDemandProcessBy>;
  directFilter?: Filter<SpendingDemandProcessBy>;
  disableQuery?: boolean;
}) => {
  const { addTokenIntheHeaderRequest, userInfo } = useUserAuth();

  addTokenIntheHeaderRequest();

  const useAllSpendingDemandOfManager = useGenericAPI<
    SpendingDemandProcessBy,
    Partial<SpendingDemandProcessBy>,
    Partial<SpendingDemandProcessBy>
  >({
    baseURL: API_BASE_URL_SPENDING_DEMAND_MANAGER,
    APIname: "spendingdemandmanager",
    pageSize: config?.pageSize || 10,
    orderBy: config?.orderBy || "-spending_demand_update_date",
    directFilter: config?.directFilter,
    disableGet: config?.disableQuery || false,
  });

  const { handleInternError, handleNetworkError } = useErrorHandler();

  const settings = {
    handleNetworkErrorFunction: handleNetworkError,
    handleInternErrorFunction: handleInternError,
    getCurrentPage: () => null,
    getCurrentFilter: () => ({}),
    employeeId: userInfo?.user_employee_id || "null",
    userId: userInfo?.user_id || "null",
    forUser: false,

    disableQuery: true,
  };
  const useSpendingDemandeCore = useSpendingDemandCore(settings);

  return {
    ...useSpendingDemandeCore,
    allSpendingDemandList: useAllSpendingDemandOfManager.getAllQuery,
    rawhookGeneric: useAllSpendingDemandOfManager,
  };
};

export const useSpendingDemandSpendingList = (
  id: string,
  config?: {
    pageSize?: number;
    orderBy?: OrderByType<Spending>;
    directFilter?: Filter<Partial<Spending>>;
    disableQuery?: boolean;
  }
) => {
  const { addTokenIntheHeaderRequest } = useUserAuth();
  const baseURL = `${BASE_URL}/api/v2/compta/spendingdemands/${id}/spendings`;
  const useSpendingDemandSpendingList = useGenericAPI<
    Spending,
    Partial<Spending>,
    Partial<Spending>
  >({
    baseURL,
    APIname: "spendingOfSpendingDemand",
    pageSize: config?.pageSize || 0,
    orderBy: config?.orderBy,
    directFilter: config?.directFilter,
    disableGet: config?.disableQuery || false,
    queryKey: [id],
  });
  addTokenIntheHeaderRequest();
  const [totalSpending, setTotalSpending] = useState<{
    totalTTC?: number;
    totalHT?: number;
    totalTotalPayed?: number;
  }>({ totalTTC: undefined, totalHT: undefined, totalTotalPayed: undefined });

  useEffect(() => {
    if (useSpendingDemandSpendingList.getAllQuery.data) {
      const data = useSpendingDemandSpendingList.getAllQuery.data.data;
      setTotalSpending({
        totalTTC: data?.reduce(
          (acc: number, spending: Spending) =>
            acc + spending.spending_amountTTC,
          0
        ),
        totalHT: data?.reduce(
          (acc: number, spending: Spending) => acc + spending.spending_amount,
          0
        ),
        totalTotalPayed: data?.reduce(
          (acc: number, spending: Spending) =>
            acc + (spending.spending_total_payed || 0),
          0
        ),
      });
    }
  }, [useSpendingDemandSpendingList.getAllQuery.data]);

  return {
    ...useSpendingDemandSpendingList,
    totalSpending,
  };
};
export const useSpendingDemandCreditList = (spendingDemandId: string, options?: { disableQuery?: boolean, pageSize?: number, orderBy?: OrderByType<Credit>, directFilter?: Filter<Partial<Credit>> }) => {
  const baseURL = `${BASE_URL}/api/v2/compta/spendingdemands/${spendingDemandId}/credits`;
  useUserAuth().addTokenIntheHeaderRequest();
  const useCreditList = useGenericAPI<Credit, Partial<Credit>, Partial<Credit>>(
    {
      baseURL,
      APIname: "creditList",
      pageSize: options?.pageSize || 0,
      orderBy: options?.orderBy || undefined,
      directFilter: options?.directFilter || undefined,
      disableGet: options?.disableQuery || false,
      queryKey: ["creditListspending", spendingDemandId],
    }
  );
  const [totalCredit, setTotalCredit] = useState<{
    totalAmount?: number;
    totalAmountAvailable?: number;
  }>({ totalAmount: undefined, totalAmountAvailable: undefined });
  useEffect(() => {
    if (useCreditList.getAllQuery.data) {
      setTotalCredit({
        totalAmount: useCreditList.getAllQuery.data.data?.reduce(
          (acc: number, credit: Credit) => acc + credit.credit_amount,
          0
        ),
        totalAmountAvailable: useCreditList.getAllQuery.data.data?.reduce(
          (acc: number, credit: Credit) =>
            acc + (credit.credit_total_available || 0),
          0
        ),
      });
    }
  }, [useCreditList.getAllQuery.data]);

  return {
    ...useCreditList,
    totalCredit,
  };
};

export const useSpendingDemandSubCreditList = (spendingDemandId: string, options?: { disableQuery?: boolean, pageSize?: number, orderBy?: OrderByType<SubCredit>, directFilter?: Filter<Partial<SubCredit>> }) => {
  const baseURL = `${BASE_URL}/api/v2/compta/spendingdemands/${spendingDemandId}/subcredits`;
  useUserAuth().addTokenIntheHeaderRequest();
  const useSubCreditList = useGenericAPI<SubCredit, Partial<SubCredit>, Partial<SubCredit>>(
    {
      baseURL,
      APIname: "subCreditList",
      pageSize: options?.pageSize || 0,
      orderBy: options?.orderBy || undefined,
      directFilter: options?.directFilter || undefined,
      disableGet: options?.disableQuery || false,
      queryKey: ["subCreditListspending", spendingDemandId],
    }
  );
  const [totalSubCredit, setTotalSubCredit] = useState<{
    totalAmount?: number;
  }>({ totalAmount: undefined });
  useEffect(() => {
    if (useSubCreditList.getAllQuery.data) {
      setTotalSubCredit({
        totalAmount: useSubCreditList.getAllQuery.data.data?.reduce(
          (acc: number, subCredit: SubCredit) => acc + subCredit.s_credit_amount,
          0
        ),
      });
    }
  }, [useSubCreditList.getAllQuery.data]);

  return {
    ...useSubCreditList,
    totalSubCredit,
  };
};

interface PaymentSummaryResponse {
  total_payed_collab: number;
  total_payed_org: number;
  total_acompte: number;
}

export const useSpendingDemandPaymentSummary = (spendingDemandId: string, options?: { disableQuery?: boolean }) => {
  const { addTokenIntheHeaderRequest } = useUserAuth();
  addTokenIntheHeaderRequest();

  const baseURL = `${BASE_URL}/api/v2/compta/spendingdemands/${spendingDemandId}/payment-summary`;

  const query = useQuery({
    queryKey: ["paymentSummary", spendingDemandId],
    queryFn: async () => {
      const response = await axios.get<PaymentSummaryResponse>(baseURL);
      return response.data;
    },
    enabled: !!spendingDemandId && !options?.disableQuery,
  });

  return {
    paymentSummary: {
      totalPayedCollab: query.data?.total_payed_collab || 0,
      totalPayedOrg: query.data?.total_payed_org || 0,
      totalAcompte: query.data?.total_acompte || 0,
    },
    isLoading: query.isLoading,
  };
};
